<template>
    <VModalWrapper :name="name" :title="title" v-on="listeners" v-bind="attributes">
        <ValidationObserver v-slot="{ handleSubmit }" v-if="itemId">
            <form class="active-form" @submit.prevent="handleSubmit(submit)">
                <p class="inventory-action-modal__hint" v-if="currentCityName">
                    Сейчас оборудование числится в городе <strong>{{ currentCityName }}</strong
                    >.
                </p>
                <p class="inventory-action-modal__hint" v-else>Город у оборудования пока не указан.</p>

                <!-- Пустой обязательный список — тупик без объяснения: у пользователя с одним
                     городом правки переместить вещь некуда. Говорим это словами. -->
                <p class="inventory-action-modal__hint" v-if="!hasTargets">
                    Других городов в вашей области правки нет — переместить оборудование некуда.
                </p>

                <!-- ГОРОДА СУЖЕНЫ ОБЛАСТЬЮ ПРАВКИ, а не просмотра. Городской пользователь
                     видит в фильтрах все города, но перевезти технику может только между
                     своими: иначе он отправил бы её туда, где сам её больше не увидит,
                     а чужой склад получил бы технику, о приезде которой никто не решал.
                     Тот же список сузит и сервер — здесь это удобство, а не защита. -->
                <ValidationProvider
                    class="active-form__field"
                    name="move-city"
                    rules="required"
                    v-slot="{ errors }"
                    v-if="hasTargets"
                >
                    <label class="active-form__label active-form__label_required" for="move-city">Куда</label>

                    <VSelect
                        id="move-city"
                        name="move-city"
                        label="name"
                        class="active-form__control control"
                        placeholder="Город назначения"
                        :clearable="false"
                        :disabled="isSaving"
                        :options="cityOptions"
                        :reduce="(city) => city.id"
                        v-model="cityId"
                    />

                    <span class="active-form__error">{{ errors[0] }}</span>
                </ValidationProvider>

                <ValidationProvider
                    class="active-form__field"
                    name="move-comment"
                    v-slot="{ errors }"
                    v-if="hasTargets"
                >
                    <label class="active-form__label" for="move-comment">Комментарий</label>

                    <VInput
                        id="move-comment"
                        name="move-comment"
                        class="active-form__control"
                        placeholder="Например: переезд офиса"
                        :disabled="isSaving"
                        v-model="comment"
                    ></VInput>

                    <span class="active-form__error">{{ errors[0] }}</span>
                </ValidationProvider>

                <p class="active-form__error inventory-action-modal__error" v-if="error">{{ error }}</p>

                <div class="active-form__actions">
                    <VButton
                        type="button"
                        class="active-form__action button_green-light"
                        :disabled="isSaving"
                        @click="hide"
                    >
                        {{ hasTargets ? 'Отмена' : 'Закрыть' }}
                    </VButton>

                    <VButton
                        type="submit"
                        class="active-form__action button_green"
                        :disabled="isSaving"
                        v-if="hasTargets"
                    >
                        {{ isSaving ? 'Сохраняем…' : 'Переместить' }}
                    </VButton>
                </div>
            </form>
        </ValidationObserver>
    </VModalWrapper>
</template>
<script>
// Components
import VInput from '../../../components/VInput/VInput.vue';
import VButton from '../../../components/VButton/VButton.vue';
import VSelect from '../../../components/VSelect/VSelect.vue';
import VModalWrapper from '../../../components/VModalWrapper/VModalWrapper.vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';

// Other
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import ModalNames from '../../../../js/enums/ModalNames';
import ModalMixin from '../../../../js/vue/mixins/ModalMixin';
import { moveInventoryItem } from '../../../../js/inventory-api';
import { errorMessage } from '../../../../js/error';

/**
 * Перемещение оборудования в другой город (7-F2A).
 *
 * ГОРОД НАЗНАЧЕНИЯ ОБЯЗАТЕЛЕН И НЕ ОЧИЩАЕТСЯ. «Переместить в никуда» — это не
 * перемещение, а потеря города; снятие города осталось только у создания.
 *
 * Текущий город в запрос не отправляется: карточка могла быть открыта час назад, и
 * сервер читает его сам — уже под блокировкой строки.
 */
@Component({
    components: {
        VInput,
        VButton,
        VSelect,
        VModalWrapper,

        ValidationObserver,
        ValidationProvider,
    },
})
class VInventoryItemMoveModal extends ModalMixin {
    @Prop({ type: String, default: ModalNames.INVENTORY_ITEM_MOVE })
    name;
    @Prop({ type: String, default: 'Перемещение оборудования' })
    defaultTitle;
    @Prop({ type: Object, required: true })
    cache;

    itemId = null;
    currentCityId = null;
    currentCityName = '';
    cityId = null;
    comment = '';
    error = '';
    isSaving = false;

    /**
     * Города, доступные на правку, без текущего: переместить туда, где вещь уже стоит,
     * нельзя, и предлагать это значит заранее обещать отказ.
     */
    get cityOptions() {
        return this.$_inventoryEditableCities(this.cache.cities ?? []).filter(
            (city) => Number(city.id) !== Number(this.currentCityId),
        );
    }

    get hasTargets() {
        return this.cityOptions.length > 0;
    }

    async submit() {
        if (this.isSaving || !this.itemId || !this.cityId) {
            return;
        }

        this.isSaving = true;
        this.error = '';

        try {
            const response = await moveInventoryItem(this.itemId, { cityId: this.cityId, comment: this.comment });

            this.resolve(response.data);
            this.hide();
        } catch (requestError) {
            this.error = errorMessage(requestError);
        } finally {
            this.isSaving = false;
        }
    }

    beforeOpen(event) {
        const params = event?.params ?? {};

        this.itemId = params.itemId ?? null;
        this.currentCityId = params.cityId ?? null;
        this.currentCityName = params.cityName || '';
        this.cityId = null;
        this.comment = '';
        this.error = '';
        this.isSaving = false;
    }

    beforeClose() {
        this.itemId = null;
        this.cityId = null;
        this.comment = '';
        this.error = '';
    }
}

export default VInventoryItemMoveModal;
</script>
